// Firestore service for Health & Fitness daily entries
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  limit,
  query,
  documentId,
  where,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

function getDailyCollectionRef(userId, entityId) {
  if (!userId || !entityId) throw new Error('userId and entityId are required');
  // Use a flat collection under the entity: health_daily/{yyyy-mm-dd}
  return collection(db, `users/${userId}/entities/${entityId}/health_daily`);
}

function getGoalsCollectionRef(userId, entityId) {
  if (!userId || !entityId) throw new Error('userId and entityId are required');
  return collection(db, `users/${userId}/entities/${entityId}/health_goals`);
}

export async function getDailyByDate(userId, entityId, dateId) {
  const ref = doc(db, `users/${userId}/entities/${entityId}/health_daily/${dateId}`);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function upsertDaily(userId, entityId, entry) {
  const { dateId, weightKg, bodyFatPct, calories, ...rest } = entry || {};
  if (!dateId) throw new Error('dateId is required (yyyy-mm-dd)');
  const ref = doc(db, `users/${userId}/entities/${entityId}/health_daily/${dateId}`);
  const now = new Date().toISOString();
  const data = {
    weightKg: Number(weightKg) || 0,
    bodyFatPct: Number(bodyFatPct) || 0,
    calories: Number(calories) || 0,
    ...rest,
    updatedAt: now,
  };
  const prev = await getDoc(ref);
  if (!prev.exists()) data.createdAt = now;
  await setDoc(ref, data, { merge: true });
  return { id: dateId, ...data };
}

export async function getYearRange(userId, entityId) {
  const col = getDailyCollectionRef(userId, entityId);
  const firstQ = query(col, orderBy(documentId(), 'asc'), limit(1));
  const lastQ = query(col, orderBy(documentId(), 'desc'), limit(1));
  const [firstSnap, lastSnap] = await Promise.all([getDocs(firstQ), getDocs(lastQ)]);
  if (firstSnap.empty || lastSnap.empty) return { minYear: null, maxYear: null, years: [] };
  const firstId = firstSnap.docs[0].id;
  const lastId = lastSnap.docs[0].id;
  const minYear = Number(String(firstId).slice(0, 4));
  const maxYear = Number(String(lastId).slice(0, 4));
  const years = [];
  for (let y = maxYear; y >= minYear; y--) years.push(y);
  return { minYear, maxYear, years };
}

export async function getDailyForYear(userId, entityId, year) {
  const col = getDailyCollectionRef(userId, entityId);
  const startId = `${year}-01-01`;
  const endId = `${year}-12-31`;
  const qy = query(
    col,
    orderBy(documentId(), 'asc'),
    where(documentId(), '>=', startId),
    where(documentId(), '<=', endId)
  );
  const snap = await getDocs(qy);
  const list = [];
  snap.forEach(d => list.push({ id: d.id, ...d.data() }));
  return list;
}

export async function getDailyRange(userId, entityId, startId, endId) {
  const col = getDailyCollectionRef(userId, entityId);
  const qy = query(
    col,
    orderBy(documentId(), 'asc'),
    where(documentId(), '>=', startId),
    where(documentId(), '<=', endId)
  );
  const snap = await getDocs(qy);
  const list = [];
  snap.forEach(d => list.push({ id: d.id, ...d.data() }));
  return list;
}

export async function bulkUpsertDaily(userId, entityId, entries = []) {
  let ok = 0, fail = 0;
  for (const e of entries) {
    try {
      // Basic validation
      if (!(e && e.dateId && /^\d{4}-\d{2}-\d{2}$/.test(e.dateId))) throw new Error('invalid dateId');
      const payload = {
        dateId: e.dateId,
        weightKg: Number(e.weightKg) || 0,
        bodyFatPct: Number(e.bodyFatPct) || 0,
        calories: Number(e.calories) || 0,
        notes: typeof e.notes === 'string' ? e.notes : undefined,
      };
      // Optional nested fields
      const chest = e.chest != null && e.chest !== '' ? Number(e.chest) : undefined;
      const waist = e.waist != null && e.waist !== '' ? Number(e.waist) : undefined;
      const hip = e.hip != null && e.hip !== '' ? Number(e.hip) : undefined;
      if (chest != null || waist != null || hip != null) {
        payload.circumferences = { chest, waist, hip };
      }
      const protein = e.protein != null && e.protein !== '' ? Number(e.protein) : undefined;
      const carbs = e.carbs != null && e.carbs !== '' ? Number(e.carbs) : undefined;
      const fat = e.fat != null && e.fat !== '' ? Number(e.fat) : undefined;
      if (protein != null || carbs != null || fat != null) {
        payload.macros = { protein, carbs, fat };
      }
      const steps = e.steps != null && e.steps !== '' ? Number(e.steps) : undefined;
      const sleepHours = e.sleepHours != null && e.sleepHours !== '' ? Number(e.sleepHours) : undefined;
      if (steps != null) payload.steps = steps;
      if (sleepHours != null) payload.sleepHours = sleepHours;

      await upsertDaily(userId, entityId, payload);
      ok++;
    } catch {
      fail++;
    }
  }
  return { ok, fail };
}

export async function deleteDaily(userId, entityId, dateId) {
  const ref = doc(db, `users/${userId}/entities/${entityId}/health_daily/${dateId}`);
  await deleteDoc(ref);
}

// Health goal management (e.g., calorieTarget), versioned by startId (yyyy-mm-dd)
export async function getGoals(userId, entityId, type) {
  const col = getGoalsCollectionRef(userId, entityId);
  const qy = query(col, orderBy('startId', 'asc'));
  const snap = await getDocs(qy);
  const list = [];
  snap.forEach(d => {
    const data = d.data();
    if (!type || data?.type === type) list.push({ id: d.id, ...data });
  });
  return list;
}

export async function upsertGoal(userId, entityId, { type, startId, value }) {
  if (!type) throw new Error('type is required');
  if (!startId || !/^\d{4}-\d{2}-\d{2}$/.test(startId)) throw new Error('startId (yyyy-mm-dd) required');
  if (!Number.isFinite(Number(value))) throw new Error('numeric value required');
  const id = `${type}_${startId}`;
  const ref = doc(db, `users/${userId}/entities/${entityId}/health_goals/${id}`);
  const now = new Date().toISOString();
  const data = { type, startId, value: Number(value), updatedAt: now };
  const prev = await getDoc(ref);
  if (!prev.exists()) data.createdAt = now;
  await setDoc(ref, data, { merge: true });
  return { id, ...data };
}


// Cross-device view settings for Health dashboard
function getSettingsDocRef(userId, entityId) {
  if (!userId || !entityId) throw new Error('userId and entityId are required');
  return doc(db, `users/${userId}/entities/${entityId}/health_settings/view`);
}

export async function getSettings(userId, entityId) {
  const ref = getSettingsDocRef(userId, entityId);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : {};
}

export async function upsertSettings(userId, entityId, partialSettings = {}) {
  const ref = getSettingsDocRef(userId, entityId);
  const now = new Date().toISOString();
  await setDoc(ref, { ...partialSettings, updatedAt: now }, { merge: true });
}

// Versioned physical targets (weight, calories, body fat range)
function getTargetsCollectionRef(userId, entityId) {
  if (!userId || !entityId) throw new Error('userId and entityId are required');
  return collection(db, `users/${userId}/entities/${entityId}/health_targets`);
}

export async function getTargets(userId, entityId, type) {
  const col = getTargetsCollectionRef(userId, entityId);
  const qy = query(col, orderBy('startId', 'asc'));
  const snap = await getDocs(qy);
  const list = [];
  snap.forEach(d => {
    const data = d.data();
    if (!type || data?.type === type) list.push({ id: d.id, ...data });
  });
  return list;
}

export async function upsertTarget(userId, entityId, { type, startId, value, min, max }) {
  if (!type) throw new Error('type is required');
  if (!startId || !/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(startId)) throw new Error('startId (yyyy-mm-dd) required');
  const id = `${type}_${startId}`;
  const ref = doc(db, `users/${userId}/entities/${entityId}/health_targets/${id}`);
  const now = new Date().toISOString();
  const payload = { type, startId, updatedAt: now };
  if (type === 'bfRange') {
    const lo = Number(min);
    const hi = Number(max);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) throw new Error('min/max required');
    payload.min = lo; payload.max = hi;
  } else {
    const v = Number(value);
    if (!Number.isFinite(v)) throw new Error('numeric value required');
    payload.value = v;
  }
  const prev = await getDoc(ref);
  if (!prev.exists()) payload.createdAt = now;
  await setDoc(ref, payload, { merge: true });
  return { id, ...payload };
}


